import React, { useState, useEffect, useRef } from 'react';
import { Users, DollarSign, TrendingUp, CreditCard, Plus, Minus, Upload, FileText, CheckCircle, AlertCircle, Phone, Bell, Trash2, UserPlus, Settings, Copy, Link, Lock, X } from 'lucide-react';
import axios from 'axios';
import EmailTemplatesEditor from './EmailTemplatesEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIP_COLORS = {
  diavelico: 'text-diavel-red',
  silver: 'text-zinc-300',
  gold: 'text-yellow-400',
  diamond: 'text-cyan-300',
};

export default function AdminPage() {
  const [tab, setTab] = useState('members');
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [balanceModal, setBalanceModal] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceDesc, setBalanceDesc] = useState('Recarga manual por admin');
  const [csvData, setCsvData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  // New: Add member modal
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', phone: '', membership_price: '100' });
  const [addMemberResult, setAddMemberResult] = useState(null);
  // New: Settings
  const [settings, setSettings] = useState({ stripe_payment_link: '', registration_open: true, membership_price: '200' });
  const [settingsMsg, setSettingsMsg] = useState(null);
  // New: Password change
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [pwMsg, setPwMsg] = useState(null);
  // Registration link
  const [linkCopied, setLinkCopied] = useState(false);
  // Membership edit modal
  const [membershipModal, setMembershipModal] = useState(null);
  const [membershipForm, setMembershipForm] = useState({ status: '', price: '', expiry: '' });
  const [membershipMsg, setMembershipMsg] = useState(null);
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  // Referral tattoo
  const [showReferralTattoo, setShowReferralTattoo] = useState(false);
  const [refTattoo, setRefTattoo] = useState({ code: '', tattoo_price: '', referred_name: '' });
  const [refTattooResult, setRefTattooResult] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, membersRes, txnRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/members`, { withCredentials: true }),
        axios.get(`${API}/admin/transactions`, { withCredentials: true }),
        axios.get(`${API}/admin/settings`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setMembers(membersRes.data.members || []);
      setTransactions(txnRes.data.transactions || []);
      if (settingsRes.data) setSettings(s => ({ ...s, ...settingsRes.data }));
    } catch {}
  };

  const handleBalanceUpdate = async () => {
    if (!balanceModal || !balanceAmount) return;
    try {
      await axios.post(`${API}/admin/balance`, { user_id: balanceModal.user_id, amount: parseFloat(balanceAmount), description: balanceDesc }, { withCredentials: true });
      setBalanceModal(null); setBalanceAmount(''); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleDeleteMember = async (userId, name) => {
    if (!window.confirm(`¿Seguro que quieres eliminar a ${name}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/members/${userId}`, { withCredentials: true });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error al eliminar'); }
  };

  const handleAddMember = async () => {
    setAddMemberResult(null);
    try {
      const res = await axios.post(`${API}/admin/members`, newMember, { withCredentials: true });
      setAddMemberResult({ type: 'success', text: `Miembro creado. Contraseña temporal: ${res.data.temp_password}` });
      setNewMember({ name: '', email: '', phone: '', membership_price: '100' });
      fetchData();
    } catch (err) {
      setAddMemberResult({ type: 'error', text: err.response?.data?.detail || 'Error' });
    }
  };

  const openMembershipEdit = (member) => {
    setMembershipModal(member);
    setMembershipForm({
      status: member.membership_status || 'pending',
      price: String(member.membership_price || 100),
      expiry: member.membership_expiry ? member.membership_expiry.split('T')[0] : '',
    });
    setMembershipMsg(null);
  };

  const handleMembershipSave = async () => {
    if (!membershipModal) return;
    setMembershipMsg(null);
    try {
      const payload = { user_id: membershipModal.user_id };
      if (membershipForm.status) payload.status = membershipForm.status;
      if (membershipForm.price) payload.price = parseFloat(membershipForm.price);
      if (membershipForm.expiry) payload.expiry = new Date(membershipForm.expiry + 'T23:59:59Z').toISOString();
      await axios.post(`${API}/admin/membership`, payload, { withCredentials: true });
      setMembershipMsg({ type: 'success', text: 'Membresia actualizada' });
      fetchData();
      setTimeout(() => setMembershipModal(null), 1000);
    } catch (err) {
      setMembershipMsg({ type: 'error', text: err.response?.data?.detail || 'Error' });
    }
  };

  const filteredMembers = members.filter(m => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q) || m.phone?.includes(q);
  });

  const handleReferralTattoo = async () => {
    setRefTattooResult(null);
    try {
      const res = await axios.post(`${API}/admin/referral-tattoo`, refTattoo, { withCredentials: true });
      setRefTattooResult({ type: 'success', text: res.data.message });
      setRefTattoo({ code: '', tattoo_price: '', referred_name: '' });
      fetchData();
    } catch (err) {
      setRefTattooResult({ type: 'error', text: err.response?.data?.detail || 'Error' });
    }
  };

  const handleSaveSettings = async () => {
    setSettingsMsg(null);
    try {
      await axios.post(`${API}/admin/settings`, settings, { withCredentials: true });
      setSettingsMsg({ type: 'success', text: 'Configuracion guardada' });
    } catch (err) {
      setSettingsMsg({ type: 'error', text: err.response?.data?.detail || 'Error' });
    }
  };

  const handleChangePassword = async () => {
    setPwMsg(null);
    try {
      await axios.post(`${API}/auth/change-password`, pwForm, { withCredentials: true });
      setPwMsg({ type: 'success', text: 'Contraseña actualizada' });
      setPwForm({ current_password: '', new_password: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.detail || 'Error' });
    }
  };

  const registrationLink = `${window.location.origin}/register`;
  const copyLink = () => {
    navigator.clipboard.writeText(registrationLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // CSV handlers
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { alert('El archivo esta vacio'); return; }
      const sep = lines[0].includes(';') ? ';' : ',';
      const headers = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/"/g, ''));
      const nameIdx = headers.findIndex(h => h.includes('nombre') || h === 'name');
      const emailIdx = headers.findIndex(h => h.includes('email') || h.includes('correo'));
      const phoneIdx = headers.findIndex(h => h.includes('tel') || h.includes('phone') || h.includes('movil') || h.includes('whatsapp'));
      if (nameIdx === -1 && emailIdx === -1) { alert('No se encontraron columnas de nombre o email.'); return; }
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(sep).map(c => c.trim().replace(/"/g, ''));
        if (cols.length < 2) continue;
        rows.push({ name: nameIdx >= 0 ? cols[nameIdx] || '' : '', email: emailIdx >= 0 ? cols[emailIdx] || '' : '', phone: phoneIdx >= 0 ? cols[phoneIdx] || '' : '' });
      }
      setCsvData({ headers, rows, fileName: file.name });
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvData?.rows?.length) return;
    setImporting(true); setImportResult(null);
    try {
      const res = await axios.post(`${API}/admin/import-csv`, { rows: csvData.rows }, { withCredentials: true });
      setImportResult(res.data);
      if (res.data.imported > 0) fetchData();
    } catch (err) { setImportResult({ error: err.response?.data?.detail || 'Error' }); }
    finally { setImporting(false); }
  };

  const TABS = [
    { id: 'members', label: 'Miembros' },
    { id: 'referrals', label: 'Referidos' },
    { id: 'transactions', label: 'Transacciones' },
    { id: 'import', label: 'Importar' },
    { id: 'emails', label: 'Emails' },
    { id: 'settings', label: 'Ajustes' },
  ];

  return (
    <div className="p-6 md:p-10 pb-24 md:pb-10" data-testid="admin-page">
      <div className="mb-10 animate-fade-in-up">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight uppercase text-white">Panel Admin</h1>
        <p className="text-zinc-500 mt-2">Gestion del INFERNO</p>
      </div>

      {/* Registration link banner */}
      <div className="bg-diavel-card border border-white/5 p-4 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-3 animate-fade-in-up">
        <div className="flex items-center gap-3">
          <Link className="w-5 h-5 text-diavel-red" />
          <div>
            <p className="text-sm font-semibold text-white">Enlace de registro del INFERNO</p>
            <p className="font-mono text-xs text-zinc-500 break-all">{registrationLink}</p>
          </div>
        </div>
        <button onClick={copyLink} data-testid="copy-registration-link"
          className="bg-diavel-red text-white font-bold uppercase tracking-wider py-2 px-6 hover:bg-red-700 transition-all duration-300 flex items-center gap-2 text-sm whitespace-nowrap">
          {linkCopied ? <><CheckCircle className="w-4 h-4" /> Copiado</> : <><Copy className="w-4 h-4" /> Copiar enlace</>}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 animate-fade-in-up animate-delay-100">
          <div className="bg-diavel-card border border-white/5 p-6">
            <Users className="w-5 h-5 text-zinc-600 mb-3" />
            <p className="font-heading font-bold text-2xl text-white" data-testid="admin-total-members">{stats.total_members}</p>
            <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mt-1">Miembros</p>
          </div>
          <div className="bg-diavel-card border border-white/5 p-6">
            <DollarSign className="w-5 h-5 text-zinc-600 mb-3" />
            <p className="font-heading font-bold text-2xl text-white">{stats.total_balance.toFixed(2)}€</p>
            <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mt-1">Saldo total</p>
          </div>
          <div className="bg-diavel-card border border-white/5 p-6">
            <CreditCard className="w-5 h-5 text-zinc-600 mb-3" />
            <p className="font-heading font-bold text-2xl text-white">{stats.total_transactions}</p>
            <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mt-1">Pagos</p>
          </div>
          <div className="bg-diavel-card border border-white/5 p-6">
            <TrendingUp className="w-5 h-5 text-zinc-600 mb-3" />
            <p className="font-heading font-bold text-2xl text-green-400">{stats.total_revenue.toFixed(2)}€</p>
            <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mt-1">Ingresos</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-8 animate-fade-in-up animate-delay-200 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`px-5 py-3 text-sm font-bold uppercase tracking-wider transition-all duration-300 ${tab === t.id ? 'bg-diavel-red text-white' : 'bg-diavel-card text-zinc-500 hover:text-white border border-white/5'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Members */}
      {tab === 'members' && (
        <div className="animate-fade-in-up">
          <div className="flex flex-col md:flex-row justify-between gap-3 mb-4">
            <input data-testid="search-members" type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="bg-zinc-900/50 border border-white/10 focus:border-red-600 h-10 px-4 text-white placeholder:text-zinc-600 outline-none text-sm md:w-80"
              placeholder="Buscar por nombre, email o telefono..." />
            <button onClick={() => setShowAddMember(true)} data-testid="add-member-btn"
              className="bg-diavel-red text-white font-bold uppercase tracking-wider py-2 px-6 hover:bg-red-700 transition-all duration-300 flex items-center gap-2 text-sm">
              <UserPlus className="w-4 h-4" /> Añadir miembro
            </button>
          </div>
          <div className="bg-diavel-card border border-white/5 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-members-table">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">Nombre</th>
                    <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">Email</th>
                    <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">Telefono</th>
                    <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">Saldo</th>
                    <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">VIP</th>
                    <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">Membresia</th>
                    <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((m, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="p-4 text-sm text-white">{m.name}</td>
                      <td className="p-4 text-sm text-zinc-400">{m.email}</td>
                      <td className="p-4 text-sm text-zinc-400">
                        {m.phone ? (
                          <a href={`https://wa.me/${m.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#25D366] hover:underline">
                            <Phone className="w-3 h-3" />{m.phone}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="p-4 text-sm font-bold text-white">{(m.balance || 0).toFixed(2)}€</td>
                      <td className="p-4"><span className={`font-mono text-xs tracking-wider uppercase ${VIP_COLORS[m.vip_level] || 'text-zinc-500'}`}>{m.vip_level}</span></td>
                      <td className="p-4">
                        <button onClick={() => openMembershipEdit(m)} data-testid={`edit-membership-${m.user_id}`}
                          className={`font-mono text-xs tracking-wider uppercase cursor-pointer hover:underline ${m.membership_status === 'active' ? 'text-green-400' : m.membership_status === 'expired' ? 'text-red-400' : 'text-yellow-400'}`}>
                          {m.membership_status || 'pending'} ({m.membership_price || 100}€)
                          {m.membership_expiry && <span className="block text-zinc-600 text-[10px]">exp: {new Date(m.membership_expiry).toLocaleDateString('es-ES')}</span>}
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setBalanceModal(m)} className="text-xs text-diavel-red hover:text-red-400 font-bold uppercase tracking-wider transition-colors">Saldo</button>
                          {m.role !== 'admin' && (
                            <button onClick={() => handleDeleteMember(m.user_id, m.name)} data-testid={`delete-member-${m.user_id}`}
                              className="text-zinc-600 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredMembers.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-600 text-sm">Sin resultados</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Referrals - Admin tattoo commission */}
      {tab === 'referrals' && (
        <div className="animate-fade-in-up space-y-8">
          <div className="bg-diavel-card border border-white/5 p-8">
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-2">Comision por Referido de Tatuaje</h3>
            <p className="text-zinc-400 text-sm mb-6">
              Un cliente trajo a alguien a tatuarse? Mete el codigo del cliente + el precio del tatuaje. 
              La app calculara el 20% y lo sumara al saldo del cliente automaticamente.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Codigo del cliente</label>
                <input data-testid="ref-tattoo-code" type="text" value={refTattoo.code}
                  onChange={e => setRefTattoo(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none font-mono tracking-wider"
                  placeholder="DV3A8F2B" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Precio del tatuaje (€)</label>
                <input data-testid="ref-tattoo-price" type="number" step="0.01" value={refTattoo.tattoo_price}
                  onChange={e => setRefTattoo(p => ({ ...p, tattoo_price: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none"
                  placeholder="280" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Nombre del referido</label>
                <input data-testid="ref-tattoo-name" type="text" value={refTattoo.referred_name}
                  onChange={e => setRefTattoo(p => ({ ...p, referred_name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none"
                  placeholder="Nombre del amigo" />
              </div>
            </div>
            {refTattoo.tattoo_price && parseFloat(refTattoo.tattoo_price) > 0 && (
              <p className="text-zinc-400 text-sm mb-4">
                20% de {parseFloat(refTattoo.tattoo_price).toFixed(2)}€ = <span className="text-green-400 font-bold">{(parseFloat(refTattoo.tattoo_price) * 0.20).toFixed(2)}€</span> para el cliente
              </p>
            )}
            <button onClick={handleReferralTattoo} data-testid="ref-tattoo-submit"
              className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-8 hover:bg-red-700 transition-all duration-300 flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Aplicar comision
            </button>
            {refTattooResult && (
              <div className={`mt-4 p-3 ${refTattooResult.type === 'success' ? 'bg-green-900/20 border border-green-600/30 text-green-400' : 'bg-red-900/20 border border-red-600/30 text-red-400'}`}>
                {refTattooResult.text}
              </div>
            )}
          </div>

          <div className="bg-diavel-card border border-white/5 p-6">
            <p className="text-zinc-400 text-sm">
              <strong className="text-white">Como funciona:</strong><br />
              <strong className="text-diavel-red">Tatuaje:</strong> El cliente da su codigo a un amigo → el amigo viene a tatuarse → tu metes el codigo + precio aqui → 20% al saldo del cliente.<br />
              <strong className="text-diavel-red">Membresia:</strong> El cliente da su codigo a un amigo → el amigo lo mete al registrarse en la app → cuando paga la membresia → 20% automatico al saldo del cliente.
            </p>
          </div>
        </div>
      )}

      {/* Transactions */}
      {tab === 'transactions' && (
        <div className="bg-diavel-card border border-white/5 overflow-hidden animate-fade-in-up">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-transactions-table">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">Usuario</th>
                  <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">Cantidad</th>
                  <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">Estado</th>
                  <th className="text-left p-4 font-mono text-xs tracking-wider uppercase text-zinc-500">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="p-4"><p className="text-sm text-white">{t.user_name}</p><p className="text-xs text-zinc-600">{t.user_email}</p></td>
                    <td className="p-4 text-sm font-bold text-white">{(t.amount || 0).toFixed(2)}€</td>
                    <td className="p-4"><span className={`font-mono text-xs tracking-wider uppercase ${t.payment_status === 'paid' ? 'text-green-400' : t.payment_status === 'expired' ? 'text-red-400' : 'text-yellow-400'}`}>{t.payment_status}</span></td>
                    <td className="p-4 font-mono text-xs text-zinc-500">{t.created_at ? new Date(t.created_at).toLocaleDateString('es-ES') : '—'}</td>
                  </tr>
                ))}
                {transactions.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-zinc-600 text-sm">Sin transacciones</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Import CSV */}
      {tab === 'import' && (
        <div className="animate-fade-in-up space-y-8">
          <div className="bg-diavel-card border border-white/5 p-8">
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-4">Importar Clientes</h3>
            <p className="text-zinc-400 text-sm mb-4">Sube un archivo CSV con columnas: <strong className="text-white">Nombre</strong>, <strong className="text-white">Email</strong>, <strong className="text-white">Telefono</strong></p>
            <input ref={fileInputRef} type="file" accept=".csv,.txt" onChange={handleFileSelect} className="hidden" data-testid="csv-file-input" />
            <button onClick={() => fileInputRef.current?.click()} data-testid="csv-upload-btn"
              className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-8 hover:bg-red-700 transition-all duration-300 flex items-center gap-2 text-sm">
              <Upload className="w-4 h-4" /> Seleccionar archivo CSV
            </button>
          </div>
          {csvData && (
            <div className="bg-diavel-card border border-white/5 p-8">
              <div className="flex items-center justify-between mb-6">
                <p className="text-zinc-500 text-sm"><FileText className="w-4 h-4 inline mr-1" />{csvData.fileName} — {csvData.rows.length} registros</p>
                <button onClick={handleImport} disabled={importing} data-testid="csv-import-btn"
                  className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-8 hover:bg-red-700 transition-all duration-300 disabled:opacity-50 flex items-center gap-2 text-sm">
                  {importing ? 'Importando...' : `Importar ${csvData.rows.length} clientes`}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-white/5"><th className="text-left p-3 font-mono text-xs text-zinc-500">#</th><th className="text-left p-3 font-mono text-xs text-zinc-500">Nombre</th><th className="text-left p-3 font-mono text-xs text-zinc-500">Email</th><th className="text-left p-3 font-mono text-xs text-zinc-500">Telefono</th></tr></thead>
                  <tbody>{csvData.rows.slice(0, 15).map((row, i) => (
                    <tr key={i} className="border-b border-white/5"><td className="p-3 text-xs text-zinc-600">{i+1}</td><td className="p-3 text-sm text-white">{row.name || <span className="text-red-400">vacio</span>}</td><td className="p-3 text-sm text-zinc-400">{row.email || <span className="text-red-400">vacio</span>}</td><td className="p-3 text-sm text-zinc-400">{row.phone || '—'}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
          {importResult && (
            <div className={`border p-6 ${importResult.error ? 'bg-red-900/20 border-red-600/30' : 'bg-green-900/20 border-green-600/30'}`}>
              {importResult.error ? <p className="text-red-400">{importResult.error}</p> : (
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div><p className="font-heading font-bold text-2xl text-green-400">{importResult.imported}</p><p className="font-mono text-xs text-zinc-500 uppercase">Importados</p></div>
                  <div><p className="font-heading font-bold text-2xl text-yellow-400">{importResult.skipped}</p><p className="font-mono text-xs text-zinc-500 uppercase">Omitidos</p></div>
                  <div><p className="font-heading font-bold text-2xl text-white">{importResult.total}</p><p className="font-mono text-xs text-zinc-500 uppercase">Total</p></div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Emails */}
      {tab === 'emails' && (
        <div className="animate-fade-in-up space-y-6">
          <div className="bg-diavel-card border border-white/5 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-2">Recordatorios de Renovacion</h3>
                <p className="text-zinc-400 text-sm">Envia recordatorios a miembros cuya membresia expira en 30 dias.</p>
              </div>
              <button onClick={async () => {
                setSendingReminders(true); setReminderResult(null);
                try { const res = await axios.post(`${API}/admin/send-renewal-reminders`, {}, { withCredentials: true }); setReminderResult(res.data); }
                catch (err) { setReminderResult({ error: err.response?.data?.detail || 'Error' }); }
                finally { setSendingReminders(false); }
              }} disabled={sendingReminders} data-testid="send-reminders-btn"
                className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-6 hover:bg-red-700 transition-all duration-300 disabled:opacity-50 flex items-center gap-2 text-sm whitespace-nowrap">
                <Bell className="w-4 h-4" /> {sendingReminders ? 'Enviando...' : 'Enviar recordatorios'}
              </button>
            </div>
            {reminderResult && <div className={`mt-4 p-3 ${reminderResult.error ? 'bg-red-900/20 border border-red-600/30 text-red-400' : 'bg-green-900/20 border border-green-600/30 text-green-400'}`}>
              {reminderResult.error ? reminderResult.error : `${reminderResult.sent} enviado(s) de ${reminderResult.total_eligible} elegible(s)`}
            </div>}
          </div>
          <EmailTemplatesEditor />
        </div>
      )}

      {/* Settings */}
      {tab === 'settings' && (
        <div className="animate-fade-in-up space-y-8">
          {/* Change password */}
          <div className="bg-diavel-card border border-white/5 p-8">
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-6 flex items-center gap-2"><Lock className="w-5 h-5" /> Cambiar Contraseña</h3>
            <div className="space-y-4 max-w-md">
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Contraseña actual</label>
                <input data-testid="current-password-input" type="password" value={pwForm.current_password} onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Nueva contraseña</label>
                <input data-testid="new-password-input" type="password" value={pwForm.new_password} onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none" />
              </div>
              <button onClick={handleChangePassword} data-testid="change-password-btn"
                className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-8 hover:bg-red-700 transition-all duration-300 text-sm">
                Cambiar contraseña
              </button>
              {pwMsg && <p className={`text-sm ${pwMsg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>{pwMsg.text}</p>}
            </div>
          </div>

          {/* Membership price */}
          <div className="bg-diavel-card border border-white/5 p-8">
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-6 flex items-center gap-2"><DollarSign className="w-5 h-5" /> Precio de Membresia</h3>
            <p className="text-zinc-400 text-sm mb-4">Este es el precio que pagaran los nuevos miembros al suscribirse. Los miembros actuales mantienen su precio original.</p>
            <div className="space-y-4 max-w-lg">
              <div className="relative">
                <input data-testid="membership-price-setting" type="number" min="1" step="1" value={settings.membership_price}
                  onChange={e => setSettings(s => ({ ...s, membership_price: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 pr-16 text-white outline-none text-lg font-bold"
                  placeholder="200" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">€/año</span>
              </div>
            </div>
          </div>

          {/* Stripe link */}
          <div className="bg-diavel-card border border-white/5 p-8">
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-6 flex items-center gap-2"><CreditCard className="w-5 h-5" /> Enlace de Stripe</h3>
            <p className="text-zinc-400 text-sm mb-4">Pega aqui tu enlace de pago de Stripe (el de los botones de tu web del club). Se usara como referencia.</p>
            <div className="space-y-4 max-w-lg">
              <input data-testid="stripe-link-input" type="url" value={settings.stripe_payment_link}
                onChange={e => setSettings(s => ({ ...s, stripe_payment_link: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none font-mono text-sm"
                placeholder="https://buy.stripe.com/..." />
            </div>
          </div>

          {/* Save all settings */}
          <div>
            <button onClick={handleSaveSettings} data-testid="save-settings-btn"
              className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-8 hover:bg-red-700 transition-all duration-300 hover:shadow-[0_0_20px_rgba(196,30,30,0.4)] text-sm">
              Guardar ajustes
            </button>
            {settingsMsg && <p className={`text-sm mt-3 ${settingsMsg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>{settingsMsg.text}</p>}
          </div>
        </div>
      )}

      {/* Add member modal */}
      {showAddMember && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="add-member-modal">
          <div className="bg-diavel-card border border-white/5 p-8 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white">Añadir Miembro</h3>
              <button onClick={() => { setShowAddMember(false); setAddMemberResult(null); }} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Nombre *</label>
                <input data-testid="add-member-name" type="text" value={newMember.name} onChange={e => setNewMember(p => ({ ...p, name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none" placeholder="Nombre y apellidos" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Email *</label>
                <input data-testid="add-member-email" type="email" value={newMember.email} onChange={e => setNewMember(p => ({ ...p, email: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none" placeholder="email@ejemplo.com" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Telefono</label>
                <input data-testid="add-member-phone" type="tel" value={newMember.phone} onChange={e => setNewMember(p => ({ ...p, phone: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none" placeholder="+34 600 000 000" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Precio membresia (€/año)</label>
                <input data-testid="add-member-price" type="number" value={newMember.membership_price} onChange={e => setNewMember(p => ({ ...p, membership_price: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none" />
              </div>
              <button onClick={handleAddMember} data-testid="confirm-add-member"
                className="w-full bg-diavel-red text-white font-bold uppercase tracking-wider py-3 hover:bg-red-700 transition-all duration-300 text-sm">
                Crear miembro
              </button>
              {addMemberResult && <p className={`text-sm ${addMemberResult.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>{addMemberResult.text}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Balance modal */}
      {balanceModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="balance-modal">
          <div className="bg-diavel-card border border-white/5 p-8 w-full max-w-md">
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-2">Gestionar saldo</h3>
            <p className="text-zinc-500 text-sm mb-6">{balanceModal.name} — Saldo actual: {(balanceModal.balance || 0).toFixed(2)}€</p>
            <div className="space-y-4">
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Cantidad</label>
                <div className="flex gap-2">
                  <button onClick={() => setBalanceAmount(prev => String(Math.abs(parseFloat(prev || 0))))} className="bg-green-900/20 text-green-400 p-3 border border-green-600/30"><Plus className="w-4 h-4" /></button>
                  <button onClick={() => setBalanceAmount(prev => String(-Math.abs(parseFloat(prev || 0))))} className="bg-red-900/20 text-red-400 p-3 border border-red-600/30"><Minus className="w-4 h-4" /></button>
                  <input data-testid="admin-balance-amount" type="number" step="0.01" value={balanceAmount} onChange={e => setBalanceAmount(e.target.value)}
                    className="flex-1 bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none" placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Descripcion</label>
                <input data-testid="admin-balance-desc" type="text" value={balanceDesc} onChange={e => setBalanceDesc(e.target.value)}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none" />
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setBalanceModal(null)} className="flex-1 bg-zinc-900 text-zinc-400 font-bold uppercase tracking-wider py-3 hover:bg-zinc-800 transition-colors text-sm">Cancelar</button>
                <button onClick={handleBalanceUpdate} data-testid="admin-balance-submit" className="flex-1 bg-diavel-red text-white font-bold uppercase tracking-wider py-3 hover:bg-red-700 transition-all duration-300 text-sm">Confirmar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Membership edit modal */}
      {membershipModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="membership-modal">
          <div className="bg-diavel-card border border-white/5 p-8 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-white">Editar Membresia</h3>
                <p className="text-zinc-500 text-sm mt-1">{membershipModal.name} — {membershipModal.email}</p>
              </div>
              <button onClick={() => setMembershipModal(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Estado</label>
                <select data-testid="membership-status-select" value={membershipForm.status}
                  onChange={e => setMembershipForm(p => ({ ...p, status: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none">
                  <option value="pending">Pendiente</option>
                  <option value="active">Activo</option>
                  <option value="expired">Expirado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Precio anual (€)</label>
                <input data-testid="membership-price-input" type="number" step="1" value={membershipForm.price}
                  onChange={e => setMembershipForm(p => ({ ...p, price: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Fecha de expiracion</label>
                <input data-testid="membership-expiry-input" type="date" value={membershipForm.expiry}
                  onChange={e => setMembershipForm(p => ({ ...p, expiry: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none" />
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setMembershipModal(null)} className="flex-1 bg-zinc-900 text-zinc-400 font-bold uppercase tracking-wider py-3 hover:bg-zinc-800 transition-colors text-sm">Cancelar</button>
                <button onClick={handleMembershipSave} data-testid="save-membership-btn"
                  className="flex-1 bg-diavel-red text-white font-bold uppercase tracking-wider py-3 hover:bg-red-700 transition-all duration-300 text-sm">Guardar</button>
              </div>
              {membershipMsg && <p className={`text-sm text-center ${membershipMsg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>{membershipMsg.text}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
